import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "soulmatch_founders_banner_dismissed_v1";

/** Sticky top banner promoting the founders launch promo (free Premium for
 *  anyone who registers before the cutoff). Self-fetches /promo/founders so
 *  the banner auto-hides the moment the cutoff passes — no redeploy needed.
 *
 *  Hidden when:
 *    - the promo is no longer active (server-side flag);
 *    - the user already has premium (irrelevant to them);
 *    - the user dismissed it (persists in localStorage).
 */
const FoundersBanner = () => {
  const { user, loading: authLoading } = useAuth();
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    // Public endpoint — no auth required, safe to call even when logged out.
    client.get("/promo/founders")
      .then((r) => setPromo(r.data))
      .catch(() => setPromo(null));
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
  };

  // Render nothing while the auth state or promo status is loading — avoids
  // a layout-shift flash on first paint.
  if (authLoading || !promo) return null;
  if (!promo.active) return null;
  if (dismissed) return null;
  // Premium-already-paying users don't need to see this — they have it.
  if (user?.is_premium && user?.subscription_status !== "founders_free") return null;

  const isFounder = user?.subscription_status === "founders_free";
  const daysLabel = promo.days_left === 1 ? "1 dia" : `${promo.days_left} dias`;

  return (
    <div
      data-testid="founders-banner"
      className="sticky top-0 z-40 w-full bg-gradient-to-r from-[#C5A880] via-[#D4B690] to-[#C5A880] text-[#0C0E12] shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Sparkles className="w-4 h-4 shrink-0" />
          <p className="text-sm md:text-[15px] font-medium leading-snug">
            {isFounder ? (
              <>
                <span className="font-bold">És Founder Member.</span>
                {" "}Tens Premium gratuito até 1 de Agosto · termina em <b>{daysLabel}</b>.
              </>
            ) : user ? (
              <>
                <span className="font-bold">Promo de lançamento.</span>
                {" "}Premium gratuito para quem se inscrever até 1 de Agosto · resta <b>{daysLabel}</b>.
              </>
            ) : (
              <>
                <span className="font-bold">Inscreve-te até 1 de Agosto</span>
                {" "}e ganha Premium gratuito · resta <b>{daysLabel}</b>.
              </>
            )}
          </p>
        </div>
        {!user && (
          <Link
            to="/register"
            data-testid="founders-banner-cta"
            className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-full bg-[#0C0E12] text-[#F5F2EE] hover:bg-[#161920] transition-colors whitespace-nowrap"
          >
            Criar conta grátis
          </Link>
        )}
        <button
          onClick={dismiss}
          aria-label="Dispensar banner"
          data-testid="founders-banner-dismiss"
          className="p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default FoundersBanner;
